import React, { useCallback, useMemo } from 'react'
import { Outlet } from 'react-router-dom'
import type { TenantThemeConfig } from '../../types/theme'
import { useWallet } from '../../contexts/WalletContext'
import { useUserInfo } from '../../contexts/UserInfoContext'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

export interface PointsOutletContext {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  getAssetUrl: (path: string) => string
  pointsText: string
  pointsBalance: number
  cashBalance: number
  refreshUserSummary: () => Promise<void>
}

interface PointsGamesLayoutProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

function getCurrentPoints(userInfo: ReturnType<typeof useUserInfo>['userInfo']): number {
  if (!userInfo) return 0
  const loyaltyPoints = Number(userInfo.loyaltyPoints?.currentPoints)
  if (Number.isFinite(loyaltyPoints)) return loyaltyPoints
  return 0
}

/** Minimal shell so points games can use useOutletContext (classic has no full points hub). */
const PointsGamesLayout: React.FC<PointsGamesLayoutProps> = ({ themeConfig, assetConfig }) => {
  const { walletData, refreshWallet } = useWallet()
  const { userInfo, refreshUserInfo } = useUserInfo()

  const getAssetUrl = useMemo(() => {
    return (path: string) => {
      const base = assetConfig.CDN_BASE_URL || ''
      return base + (path.startsWith('/') ? path : `/${path}`)
    }
  }, [assetConfig.CDN_BASE_URL])

  const refreshUserSummary = useCallback(async () => {
    await Promise.all([refreshWallet(), refreshUserInfo()])
  }, [refreshWallet, refreshUserInfo])

  const pointsBalance = getCurrentPoints(userInfo)
  const cashBalance = Number(walletData?.balance ?? 0)
  const context: PointsOutletContext = {
    themeConfig,
    assetConfig,
    getAssetUrl,
    pointsText: `${pointsBalance.toFixed(2)} Pts`,
    pointsBalance,
    cashBalance,
    refreshUserSummary,
  }

  return <Outlet context={context} />
}

export default PointsGamesLayout
